import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toPng } from "html-to-image";

/** Jigsaw Muse App Store / Play Store icon canvas. */
export const APP_ICON_SIZE = 512;
const EMAIL_ICON_SIZE = 64;

const SAVE_BAR_H = 88;
const APP_ICON_FILE_NAME = "jigsaw-muse-app-icon-512x512.png";
const EMAIL_ICON_FILE_NAME = "jigsaw-muse-app-icon-64x64.png";
const PUBLIC = process.env.PUBLIC_URL || "";

function resizePngDataUrl(dataUrl, size) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new Error("Could not scale icon"));
                return;
            }
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, size, size);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => reject(new Error("Could not scale icon"));
        img.src = dataUrl;
    });
}

/** Golden “M” jigsaw piece on twilight purple — matches App Store icon prompt. */
export const JIGSAW_APP_ICON_ART = `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`;

/**
 * Jigsaw Muse app icon capture — 512×512 edge-to-edge.
 * Premium 3D golden jigsaw piece with embossed M on dark twilight purple.
 */
export default function AppIconPage() {
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(Math.min(1, availableW / APP_ICON_SIZE, availableH / APP_ICON_SIZE));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleDownloadPng = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                width: APP_ICON_SIZE,
                height: APP_ICON_SIZE,
                pixelRatio: 1,
                cacheBust: true,
                backgroundColor: "#1a0a2e",
            });
            const link = document.createElement("a");
            link.download = APP_ICON_FILE_NAME;
            link.href = dataUrl;
            link.click();
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    const handleDownloadEmailPng = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                width: APP_ICON_SIZE,
                height: APP_ICON_SIZE,
                pixelRatio: 1,
                cacheBust: true,
                backgroundColor: "#1a0a2e",
            });
            const smallUrl = await resizePngDataUrl(dataUrl, EMAIL_ICON_SIZE);
            const link = document.createElement("a");
            link.download = EMAIL_ICON_FILE_NAME;
            link.href = smallUrl;
            link.click();
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#0f0a18",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(APP_ICON_SIZE * previewScale),
                    height: Math.round(APP_ICON_SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="jigsaw-app-icon"
                    sx={{
                        width: APP_ICON_SIZE,
                        height: APP_ICON_SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#1a0a2e",
                        lineHeight: 0,
                    }}
                >
                    <Box
                        component="img"
                        src={JIGSAW_APP_ICON_ART}
                        alt="Jigsaw Muse app icon"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            objectPosition: "center",
                            display: "block",
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    minHeight: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    py: 1,
                    bgcolor: "rgba(15, 10, 24, 0.96)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.25)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.75)" }}>
                    Jigsaw Muse · App icon · {APP_ICON_SIZE}×{APP_ICON_SIZE} · email {EMAIL_ICON_SIZE}×{EMAIL_ICON_SIZE}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={
                            saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                        }
                        onClick={handleDownloadPng}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: "#a78bfa",
                            color: "#1e1b4b",
                            "&:hover": { bgcolor: "#c4b5fd" },
                        }}
                    >
                        Download PNG
                    </Button>
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={
                            saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                        }
                        onClick={handleDownloadEmailPng}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#c4b5fd",
                            borderColor: "rgba(167, 139, 250, 0.7)",
                            "&:hover": { borderColor: "#c4b5fd", bgcolor: "rgba(167, 139, 250, 0.12)" },
                        }}
                    >
                        Download 64×64 (email)
                    </Button>
                </Box>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
